import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { listUserNotifications, markAllNotificationsRead, markNotificationRead } from '../services/notificationService';

function Notifications() {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadNotifications = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      setNotifications(await listUserNotifications(currentUser.uid));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  async function handleRead(notification) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await markNotificationRead(currentUser.uid, notification.id);
      setMessage('Notification marked read.');
      await loadNotifications();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleReadAll() {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await markAllNotificationsRead(currentUser.uid, notifications);
      setMessage('All notifications marked read.');
      await loadNotifications();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const unreadCount = notifications.filter((notification) => !notification.read).length;

  return (
    <>
      <SEO title="Notifications" />
      <section className="page-heading">
        <p className="eyebrow">Notifications</p>
        <h1>Problem activity, comments, and moderation updates.</h1>
        <p>Unread count: {unreadCount}. Notifications are private to your account.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="surface-card">
        <div className="section-toolbar">
          <div>
            <h2>Inbox</h2>
            <p className="muted">Problem contact requests, accepted contacts, comments, messages, and report updates appear here.</p>
          </div>
          <button className="button button-secondary" type="button" disabled={saving || unreadCount === 0} onClick={handleReadAll}>Mark all read</button>
        </div>
        {loading ? <p>Loading notifications...</p> : null}
        {!loading && notifications.length === 0 ? <p className="muted">No notifications yet.</p> : null}
        <div className="item-list notification-list">
          {notifications.map((notification) => (
            <article className={`content-item notification-item${notification.read ? '' : ' unread'}`} key={notification.id}>
              <p className="tag">{notification.type}{notification.read ? '' : ' · unread'}</p>
              <h3>{notification.title}</h3>
              <p>{notification.body}</p>
              <p className="muted">From {notification.actorName}</p>
              <div className="button-row compact-row">
                {notification.href ? <Link className="button button-secondary button-small" to={notification.href}>Open</Link> : null}
                {!notification.read ? <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleRead(notification)}>Mark read</button> : null}
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export default Notifications;
